import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { 
  Calendar, 
  Clock, 
  User, 
  Search,
  Filter,
  ArrowRight,
  Tag,
  BookOpen,
  Heart,
  Share2,
  Eye,
  MessageCircle,
  Star,
  TrendingUp
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/SEO/SEOHead';
import { blogs, blogUtils, blogCategories, popularTags } from '../data/blogs';
import { getBlogsFromAPI } from '../utils/blogStorageHybrid';
import { getBlogTranslation } from '../i18n/locales/blogTranslations';

export default function BlogPage() {
  const { t, currentLanguage } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(t('blog.categories.all'));
  const [selectedPost, setSelectedPost] = useState(null);
  const [sortBy, setSortBy] = useState('latest');
  const navigate = useNavigate();
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  // Получаем статические блоги и блоги из API
  const staticBlogs = blogs; // Используем исходные данные из blogs.js
  const [adminBlogs, setAdminBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Загружаем админ блоги при монтировании
  useEffect(() => {
    const loadAdminBlogs = async () => {
      try {
        const blogs = await getBlogsFromAPI();
        setAdminBlogs(blogs);
      } catch (error) {
        console.error('Ошибка загрузки админ блогов:', error);
        setAdminBlogs([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadAdminBlogs();
  }, []);
  
  // Фильтруем админ блоги, исключая статические (id 1-5)
  const filteredAdminBlogs = adminBlogs.filter(blog => !blog.id || blog.id > 5);
  
  // Объединяем все блоги без дублирования
  const allBlogs = [...staticBlogs, ...filteredAdminBlogs];
  const publishedBlogs = allBlogs.filter(blog => {
    // Для статических блогов (id 1-5) показываем на обоих языках
    if (blog.id && blog.id <= 5) {
      return blog.status === 'published';
    }
    // Для админ блогов проверяем статус и язык
    return blog.status === 'published' && blog.language === currentLanguage;
  });
  
  const featuredBlogs = allBlogs.filter(blog => {
    if (blog.id && blog.id <= 5) {
      return blog.featured && blog.status === 'published';
    }
    return blog.featured && blog.status === 'published' && blog.language === currentLanguage;
  });
  
  const latestBlogs = publishedBlogs
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
    .slice(0, 6);
  const popularBlogs = publishedBlogs
    .sort((a, b) => b.views - a.views)
    .slice(0, 6);

  // Категории включая "Все"
  const allCategories = [t('blog.categories.all'), ...blogCategories.map(cat => t(`blog.categories.${cat.id}`))];

  // Функция фильтрации блогов
  const getFilteredBlogs = () => {
    let filtered = publishedBlogs;

    // Фильтр по поиску
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(blog => {
        const tagsArray = blog.tags ? (Array.isArray(blog.tags) ? blog.tags : blog.tags.split(',').map(tag => tag.trim())) : [];
        return blog.title.toLowerCase().includes(query) ||
               blog.excerpt.toLowerCase().includes(query) ||
               tagsArray.some(tag => tag.toLowerCase().includes(query));
      });
    }

    // Фильтр по категории
    if (selectedCategory !== t('blog.categories.all')) {
      const categoryId = blogCategories.find(cat => t(`blog.categories.${cat.id}`) === selectedCategory)?.id;
      if (categoryId) {
        filtered = filtered.filter(blog => blog.category === categoryId);
      }
    }

    // Сортировка
    switch (sortBy) {
      case 'latest':
        filtered = filtered.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
        break;
      case 'popular':
        filtered = filtered.sort((a, b) => b.views - a.views);
        break;
      case 'featured':
        filtered = filtered.filter(blog => blog.featured);
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredBlogs = getFilteredBlogs();


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryName = (categoryId) => {
    const category = blogCategories.find(cat => cat.id === categoryId);
    return category ? t(`blog.categories.${categoryId}`) : t('blog.categories.no_category');
  };

  const getCategoryColor = (categoryId) => {
    const category = blogCategories.find(cat => cat.id === categoryId);
    return category ? category.color : 'bg-gray-100 text-gray-800';
  };

  const renderTranslatedContent = (postId) => {
    const content = getBlogTranslation(postId, 'content', currentLanguage);
    if (content && typeof content === 'object') {
      // Для статьи 1 (гипнотерапия для детей)
      if (postId === 1) {
        return `
          <div class="space-y-8">
            <h2 class="text-3xl font-bold mb-6">${content.main_title}</h2>
            <p class="text-lg leading-relaxed mb-8">${content.intro}</p>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.basics_title}</h3>
              <p class="text-lg leading-relaxed">${content.basics_text}</p>
            </div>
            
            <h3 class="text-xl font-semibold mb-6">${content.process_title}</h3>
            
            <div class="space-y-6">
              <div class="space-y-4">
                <h4 class="text-lg font-semibold mb-3">${content.step1_title}</h4>
                <p class="text-lg leading-relaxed">${content.step1_text}</p>
              </div>
              
              <div class="space-y-4">
                <h4 class="text-lg font-semibold mb-3">${content.step2_title}</h4>
                <p class="text-lg leading-relaxed">${content.step2_text}</p>
              </div>
              
              <div class="space-y-4">
                <h4 class="text-lg font-semibold mb-3">${content.step3_title}</h4>
                <p class="text-lg leading-relaxed">${content.step3_text}</p>
              </div>
              
              <div class="space-y-4">
                <h4 class="text-lg font-semibold mb-3">${content.step4_title}</h4>
                <ul class="space-y-3 text-lg leading-relaxed">
                  <li><strong>${content.step4_regression.split(':')[0]}:</strong> ${content.step4_regression.split(':')[1]}</li>
                  <li><strong>${content.step4_suggestions.split(':')[0]}:</strong> ${content.step4_suggestions.split(':')[1]}</li>
                  <li><strong>${content.step4_resources.split(':')[0]}:</strong> ${content.step4_resources.split(':')[1]}</li>
                  <li><strong>${content.step4_anchoring.split(':')[0]}:</strong> ${content.step4_anchoring.split(':')[1]}</li>
                </ul>
              </div>
              
              <div class="space-y-4">
                <h4 class="text-lg font-semibold mb-3">${content.step5_title}</h4>
                <p class="text-lg leading-relaxed">${content.step5_text}</p>
              </div>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.journeys_title}</h3>
              <p class="text-lg leading-relaxed mb-4">${content.journeys_text}</p>
              
              <div class="bg-blue-50 border-l-4 border-blue-400 p-6 my-6 rounded-r-lg">
                <p class="text-blue-800 text-lg"><strong>${content.journey_example}</strong></p>
              </div>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.parents_title}</h3>
              <p class="text-lg leading-relaxed mb-4">${content.parents_text}</p>
              <ul class="space-y-3 text-lg leading-relaxed">
                <li><strong>${content.parents_reinforcement.split(':')[0]}:</strong> ${content.parents_reinforcement.split(':')[1]}</li>
                <li><strong>${content.parents_communication.split(':')[0]}:</strong> ${content.parents_communication.split(':')[1]}</li>
                <li><strong>${content.parents_example.split(':')[0]}:</strong> ${content.parents_example.split(':')[1]}</li>
              </ul>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.additional_title}</h3>
              <ul class="space-y-3 text-lg leading-relaxed">
                <li><strong>${content.additional_combination.split(':')[0]}:</strong> ${content.additional_combination.split(':')[1]}</li>
                <li><strong>${content.additional_homeopathy.split(':')[0]}:</strong> ${content.additional_homeopathy.split(':')[1]}</li>
                <li><strong>${content.additional_regularity.split(':')[0]}:</strong> ${content.additional_regularity.split(':')[1]}</li>
              </ul>
            </div>
          </div>
        `;
      }
      
      // Для статьи 2 (психосоматика и кожа)
      if (postId === 2) {
        return `
          <div class="space-y-8">
            <h2 class="text-3xl font-bold mb-6">${content.main_title}</h2>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.stress_title}</h3>
              <p class="text-lg leading-relaxed">${content.stress_text}</p>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.emotions_title}</h3>
              <p class="text-lg leading-relaxed">${content.emotions_text}</p>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.conflicts_title}</h3>
              <p class="text-lg leading-relaxed">${content.conflicts_text}</p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.hypnosis_title}</h2>
            <p class="text-lg leading-relaxed mb-8">${content.hypnosis_text}</p>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.achievements_title}</h3>
              <ul class="space-y-4 text-lg leading-relaxed">
                ${content.achievements_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
              </ul>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.how_works_title}</h3>
              <p class="text-lg leading-relaxed">${content.how_works_text}</p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.success_title}</h2>
            <p class="text-lg leading-relaxed mb-8">${content.success_text}</p>
            
            <div class="bg-blue-50 border-l-4 border-blue-400 p-6 my-8 rounded-r-lg">
              <p class="text-blue-800 font-medium text-lg">${content.important_note}</p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.program_title}</h2>
            
            <div class="space-y-8">
              <div class="space-y-4">
                <h3 class="text-xl font-semibold mb-4">${content.step1_title}</h3>
                <p class="text-lg leading-relaxed"><strong>${content.step1_text.split(':')[0]}:</strong> ${content.step1_text.split(':')[1]}</p>
              </div>
              
              <div class="space-y-4">
                <h3 class="text-xl font-semibold mb-4">${content.step2_title}</h3>
                <ul class="space-y-3 text-lg leading-relaxed">
                  ${content.step2_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
                </ul>
              </div>
              
              <div class="space-y-4">
                <h3 class="text-xl font-semibold mb-4">${content.step3_title}</h3>
                <p class="text-lg leading-relaxed"><strong>${content.step3_text.split(':')[0]}:</strong> ${content.step3_text.split(':')[1]}</p>
              </div>
              
              <div class="space-y-4">
                <h3 class="text-xl font-semibold mb-4">${content.step4_title}</h3>
                <ul class="space-y-3 text-lg leading-relaxed">
                  ${content.step4_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
                </ul>
              </div>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.aspects_title}</h2>
            <ul class="space-y-4 text-lg leading-relaxed">
              ${content.aspects_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
            </ul>
            
            <div class="bg-yellow-50 border-l-4 border-yellow-400 p-6 my-8 rounded-r-lg">
              <p class="text-yellow-800 text-lg"><strong>${content.disclaimer}</strong></p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.conclusion_title}</h2>
            <p class="text-lg leading-relaxed mb-6">${content.conclusion_text}</p>
            
            <p class="text-lg leading-relaxed">${content.cta_text}</p>
            
            <div class="mt-12">
              <h2 class="text-3xl font-bold mb-6 text-center">${content.results_title}</h2>
              <p class="text-lg leading-relaxed mb-8 text-center text-gray-600">
                ${content.results_text}
              </p>
            </div>
          </div>
        `;
      }
      
      // Для статьи 3 (история г-жи Мюллер)
      if (postId === 3) {
        return `
          <div class="space-y-8">
            <div class="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
              <p class="text-blue-800 text-lg font-medium">
                <strong>${content.practical_example}</strong><br>
                <span class="text-sm text-blue-600">${content.updated}</span>
              </p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.main_title}</h2>
            
            <div class="space-y-6">
              <p class="text-lg leading-relaxed">
                ${content.intro}
              </p>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.beginning_title}</h3>
            <p class="text-lg leading-relaxed mb-6">
              ${content.beginning_text}
            </p>
            
            <h3 class="text-xl font-semibold mb-4">${content.transformation_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.transformation_text}
              </p>
              
              <div class="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
                <h4 class="text-lg font-semibold text-green-800 mb-3">${content.achievements_title}</h4>
                <ul class="space-y-2 text-green-700">
                  ${content.achievements_items.map(item => `<li>• ${item}</li>`).join('')}
                </ul>
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.results_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.results_text}
              </p>
              
              <div class="bg-rose-50 border-l-4 border-rose-400 p-6 rounded-r-lg">
                <p class="text-rose-800 text-lg font-medium">
                  <strong>${content.today_text}</strong>
                </p>
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.lessons_title}</h3>
            <div class="space-y-4">
              <ul class="space-y-3 text-lg leading-relaxed">
                ${content.lessons_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
              </ul>
            </div>
            
            <div class="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
              <p class="text-yellow-800 text-lg">
                <strong>${content.important_note}</strong>
              </p>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.ready_title}</h3>
            <p class="text-lg leading-relaxed mb-6">
              ${content.ready_text}
            </p>
          </div>
        `;
      }
      
      // Для статьи 4 (панические атаки)
      if (postId === 4) {
        return `
          <div class="space-y-8">
            <h2 class="text-3xl font-bold mb-6">${content.main_title}</h2>
            
            <div class="space-y-6">
              <p class="text-lg leading-relaxed">
                ${content.intro}
              </p>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.features_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.features_text}
              </p>
              
              <div class="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                <h4 class="text-lg font-semibold text-red-800 mb-3">${content.signs_title}</h4>
                <ul class="space-y-2 text-red-700">
                  ${content.signs_items.map(item => `<li>• ${item}</li>`).join('')}
                </ul>
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.therapy_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.therapy_text}
              </p>
              
              <div class="grid md:grid-cols-2 gap-6">
                <div class="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 class="text-lg font-semibold text-blue-800 mb-3">${content.triggers_title}</h4>
                  <p class="text-blue-700">
                    ${content.triggers_text}
                  </p>
                </div>
                
                <div class="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h4 class="text-lg font-semibold text-green-800 mb-3">${content.restructuring_title}</h4>
                  <p class="text-green-700">
                    ${content.restructuring_text}
                  </p>
                </div>
              </div>
            </div>
          </div>
        `;
      }
      
      // Для статьи 5 (гипноз)
      if (postId === 5) {
        return `
          <div class="space-y-8">
            <div class="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
              <p class="text-blue-800 text-lg font-medium">
                <strong>${content.updated}</strong>
              </p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.main_title}</h2>
            
            <div class="space-y-6">
              <p class="text-lg leading-relaxed">
                ${content.intro}
              </p>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.how_helps_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.how_helps_text}
              </p>
              
              <div class="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
                <h4 class="text-lg font-semibold text-green-800 mb-3">${content.what_happens_title}</h4>
                <ul class="space-y-2 text-green-700">
                  ${content.what_happens_items.map(item => `<li>• ${item}</li>`).join('')}
                </ul>
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.results_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.results_text}
              </p>
              
              <div class="grid md:grid-cols-2 gap-6">
                <div class="bg-purple-50 border border-purple-200 rounded-xl p-6">
                  <h4 class="text-lg font-semibold text-purple-800 mb-3">${content.new_opportunities_title}</h4>
                  <ul class="space-y-2 text-purple-700">
                    ${content.new_opportunities_items.map(item => `<li>• ${item}</li>`).join('')}
                  </ul>
                </div>
                
                <div class="bg-orange-50 border border-orange-200 rounded-xl p-6">
                  <h4 class="text-lg font-semibold text-orange-800 mb-3">${content.personal_growth_title}</h4>
                  <ul class="space-y-2 text-orange-700">
                    ${content.personal_growth_items.map(item => `<li>• ${item}</li>`).join('')}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        `;
      }
    }
    return selectedPost.content;
  };

  return (
    <div className="min-h-screen bg-white text-dark-900">
      <SEOHead 
        title={t('blog.title')}
        description={t('blog.description')}
        keywords={t('blog.keywords')}
      />
      <Header />
      <main>
        {/* Поиск и фильтры */}
        <section className="py-8 sm:py-12 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="space-y-4 sm:space-y-6"
            >
              {/* Поиск и сортировка */}
              <motion.div variants={item} className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-between">
                {/* Поиск */}
                <div className="relative flex-1 max-w-md w-full">
                  <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input
                    type="text"
                    placeholder={t('blog.search_placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>

                {/* Сортировка */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  >
                    <option value="latest">{t('blog.sort_options.latest')}</option>
                    <option value="popular">{t('blog.sort_options.popular')}</option>
                    <option value="featured">{t('blog.sort_options.featured')}</option>
                  </select>
                </div>
              </motion.div>

              {/* Категории */}
              <motion.div variants={item} className="flex flex-wrap gap-2">
                {allCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                      selectedCategory === category
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </motion.div>

              {/* Популярные теги */}
              <motion.div variants={item} className="flex flex-wrap gap-2">
                <span className="text-xs sm:text-sm text-gray-500 mr-2">{t('blog.popular_tags')}</span>
                {popularTags.slice(0, 8).map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setSearchTerm(tag)}
                    className="px-2 sm:px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                  >
                    #{getBlogTranslation(tag, 'tag', currentLanguage)}
                  </button>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Статьи */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-spacious">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {filteredBlogs.map((blog, index) => (
                <motion.article
                  key={blog.id}
                  initial={{ opacity: 1, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-elevated overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300"
                  onClick={() => setSelectedPost(blog)}
                >
                  {/* Изображение */}
                  <div className="h-40 sm:h-48 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center relative overflow-hidden">
                    {blog.featuredImage ? (
                      <img
                        src={blog.featuredImage}
                        alt={getBlogTranslation(String(blog.id), 'title', currentLanguage) || blog.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Если изображение не загрузилось, показываем заглушку
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`${blog.featuredImage ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
                      <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-blue-600" />
                    </div>
                    {blog.featured ? (
                      <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                        <Star className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 fill-current" />
                      </div>
                    ) : null}
                  </div>

                  {/* Контент */}
                  <div className="p-4 sm:p-6">
                    {/* Категория */}
                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                      <Tag className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                      <span className={`text-xs sm:text-sm font-medium px-2 py-1 rounded-full ${getCategoryColor(blog.category)}`}>
                        {getCategoryName(blog.category)}
                      </span>
                    </div>

                    {/* Заголовок */}
                    <h2 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-dark-900 overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>
                      {getBlogTranslation(String(blog.id), 'title', currentLanguage) || blog.title}
                    </h2>

                    {/* Описание */}
                    <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'}}>
                      {getBlogTranslation(String(blog.id), 'excerpt', currentLanguage) || blog.excerpt}
                    </p>

                    {/* Метаинформация */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{formatDate(blog.publishedAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{blog.readingTime} {t('blog.article.reading_time')}</span>
                      </div>
                    </div>

                    {/* Статистика */}
                    <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                      <div className="flex items-center gap-1">
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{blog.views}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{blog.likes}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{blog.featured ? t('blog.article.featured') : t('blog.article.regular')}</span>
                      </div>
                    </div>

                    {/* Теги */}
                    <div className="flex flex-wrap gap-1 mb-3 sm:mb-4">
                    {blog.tags && (Array.isArray(blog.tags) ? blog.tags : blog.tags.split(',').map(tag => tag.trim())).slice(0, 3).map((tag) => (
                      <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                        #{getBlogTranslation(tag, 'tag', currentLanguage)}
                      </span>
                    ))}
                    </div>

                    {/* Кнопка */}
                    <button 
                      onClick={() => navigate(getLocalizedPath(`/blog/${blog.slug}`))}
                      className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base"
                    >
                      {t('blog.article.read_more')}
                      <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>

            {filteredBlogs.length === 0 && (
              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="text-center py-12 sm:py-16"
              >
                <motion.div variants={item}>
                  <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">
                    {t('blog.no_articles.title')}
                  </h3>
                  <p className="text-gray-500 text-sm sm:text-base">
                    {t('blog.no_articles.description')}
                  </p>
                </motion.div>
              </motion.div>
            )}
          </div>
        </section>

        {/* Призыв к действию */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center"
            >
              <motion.div variants={item} className="block-primary text-center">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-dark-900">
                  {t('blog.cta.title')}
                </h3>
                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                  {t('blog.cta.description')}
                </p>
                <a
                  href={getLocalizedPath("/contact")}
                  className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base"
                >
                  {t('blog.cta.button')}
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Модальное окно с полной статьей */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-4 sm:mb-6">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                  <span className={`text-xs sm:text-sm font-medium px-2 py-1 rounded-full ${getCategoryColor(selectedPost.category)}`}>
                    {getCategoryName(selectedPost.category)}
                  </span>
                  {selectedPost.featured ? (
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 fill-current" />
                      <span className="text-xs text-yellow-600 font-medium">{t('blog.article.featured')}</span>
                    </div>
                  ) : null}
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-dark-900">{getBlogTranslation(String(selectedPost.id), 'title', currentLanguage) || selectedPost.title}</h2>
              </div>
              <button
                onClick={() => setSelectedPost(null)}
                className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl flex-shrink-0"
              >
                ×
              </button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
              <div className="flex items-center gap-2">
                <User className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{t('blog.article.author')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{formatDate(selectedPost.publishedAt)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{selectedPost.readingTime} {t('blog.article.minutes_reading')}</span>
              </div>
            </div>

            <div className="prose prose-sm sm:prose-lg max-w-none mb-6 sm:mb-8">
              <div dangerouslySetInnerHTML={{ __html: renderTranslatedContent(selectedPost.id) }} />
            </div>

            {/* Теги */}
            <div className="mb-4 sm:mb-6">
              <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">{t('blog.article.tags_label')}</h4>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {selectedPost.tags && (Array.isArray(selectedPost.tags) ? selectedPost.tags : selectedPost.tags.split(',').map(tag => tag.trim())).map((tag) => (
                  <span key={tag} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                    #{getBlogTranslation(tag, 'tag', currentLanguage)}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-200">
              <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{selectedPost.views} {t('blog.article.views')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{selectedPost.likes} {t('blog.article.likes')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{selectedPost.featured ? t('blog.article.featured') : t('blog.article.regular')}</span>
                </div>
              </div>
              <button className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-xs sm:text-sm">
                <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
                {t('blog.article.share')}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
}
